// Terms data managed via TanStack Query
// Provides query options and hooks for fetching terms from legacy Aspirex system

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { trpcClient } from "../trpc";
import { projectsKeys } from "./projects";

// ============================================================================
// Query Keys
// ============================================================================

export const termsKeys = {
  all: ["terms"] as const,
  basicTerms: (organizationId: string, secureUrl: string) =>
    [...termsKeys.all, "basic", organizationId, secureUrl] as const,
  standardBriefEnhancements: (organizationId: string, secureUrl: string) =>
    [...termsKeys.all, "standardBrief", organizationId, secureUrl] as const,
};

// ============================================================================
// Types - Inferred from API response
// ============================================================================

type BasicTermsResult = Awaited<
  ReturnType<typeof trpcClient.terms.getBasicTerms.query>
>;

export type BasicTerms = NonNullable<BasicTermsResult["terms"]>;
export type BasicTermsDeliverable = BasicTerms["deliverables"][number];

type StandardBriefEnhancementsResult = Awaited<
  ReturnType<typeof trpcClient.terms.getStandardBriefEnhancements.query>
>;

export type StandardBriefEnhancements = NonNullable<
  StandardBriefEnhancementsResult["enhancements"]
>;

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for fetching basic terms by secureUrl.
 */
export function basicTermsQueryOptions(
  organizationId: string,
  secureUrl: string,
) {
  return queryOptions({
    queryKey: termsKeys.basicTerms(organizationId, secureUrl),
    queryFn: () =>
      trpcClient.terms.getBasicTerms.query({ organizationId, secureUrl }),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!secureUrl,
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for fetching basic terms.
 */
export function useBasicTermsQuery(organizationId: string, secureUrl: string) {
  return useQuery(basicTermsQueryOptions(organizationId, secureUrl));
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for agreeing to basic terms.
 */
export function useAgreeToBasicTermsMutation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agreementId,
      memberName,
    }: {
      agreementId: string;
      memberName: string;
    }) =>
      trpcClient.terms.agreeToBasicTerms.mutate({
        organizationId,
        agreementId,
        memberName,
      }),
    onSuccess: () => {
      // Invalidate all terms queries to refetch updated state
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
}

// ============================================================================
// Standard Brief Query Options & Hooks
// ============================================================================

/**
 * Query options for fetching standard brief enhancements by secureUrl.
 */
export function standardBriefEnhancementsQueryOptions(
  organizationId: string,
  secureUrl: string,
) {
  return queryOptions({
    queryKey: termsKeys.standardBriefEnhancements(organizationId, secureUrl),
    queryFn: () =>
      trpcClient.terms.getStandardBriefEnhancements.query({
        organizationId,
        secureUrl,
      }),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!secureUrl,
  });
}

/**
 * Hook for fetching standard brief enhancements.
 *
 * Optional `pollWhilePpaPending` enables 5-second polling while a Paid
 * Partnership Ads authorization is outstanding — matches the legacy UCE
 * cadence so the pre-sign gate flips as soon as the creator approves on
 * their Instagram app. Polling stops automatically once
 * `igPartnershipAdsAccessCompleted` flips true.
 */
export function useStandardBriefEnhancementsQuery(
  organizationId: string,
  secureUrl: string,
  options?: { pollWhilePpaPending?: boolean },
) {
  return useQuery({
    ...standardBriefEnhancementsQueryOptions(organizationId, secureUrl),
    refetchInterval: options?.pollWhilePpaPending
      ? (query) => {
          const enhancements = query.state.data?.enhancements;
          if (!enhancements) return false;
          if (
            enhancements.requireIgPartnershipAdsAccess &&
            !enhancements.igPartnershipAdsAccessCompleted
          ) {
            return 5000;
          }
          return false;
        }
      : false,
  });
}

/**
 * Shape returned by the `terms.requestIgPartnershipAdsAccess` tRPC mutation.
 * Explicit because the inferred tRPC client return type widens to `unknown`
 * in this app (same pattern as other mutations here — see EPD-7754).
 */
export type RequestIgPartnershipAdsAccessResult = {
  success: boolean;
  errorMsg: string | null;
};

/**
 * Hook that triggers the Instagram Paid Partnership Ads authorize flow for a
 * standard brief. Backend fires Meta's partnership-ad API and the creator
 * receives the approval notification on their IG app.
 *
 * Authenticated via the creator's Better Auth session → Firebase JWT (EPD-7763).
 * Aspirex resolves project ownership server-side from the authed email.
 *
 * Business-rule outcomes (`brand_permissions`, `professional_account_needed`,
 * generic failures) come back as `{success: false, errorMsg}` so the caller
 * can render error-code-specific copy. On `success: true` we invalidate
 * every standard-brief-enhancements query under this organization so the
 * polling loop picks up `igPartnershipAdsAccessCompleted` as soon as the
 * creator approves on IG.
 */
export function useRequestIgPartnershipAdsAccessMutation(
  organizationId: string,
  profileId: string,
) {
  const queryClient = useQueryClient();

  return useMutation<
    RequestIgPartnershipAdsAccessResult,
    Error,
    { projectId: string; agreementId: string }
  >({
    mutationFn: async ({ projectId, agreementId }) =>
      (await trpcClient.terms.requestIgPartnershipAdsAccess.mutate({
        organizationId,
        projectId,
        agreementId,
        profileId,
      })) as RequestIgPartnershipAdsAccessResult,
    onSuccess: (data) => {
      if (data.success) {
        // Broad invalidation — cheaper than threading the per-brief cache
        // key down into the mutation and keeps the hook secureUrl-free.
        queryClient.invalidateQueries({
          queryKey: termsKeys.all,
        });
      }
    },
  });
}

/**
 * Shape returned by the `terms.updateIgPartnershipAdsAccess` tRPC mutation.
 * Explicit for the same reason as `RequestIgPartnershipAdsAccessResult`.
 */
export type UpdateIgPartnershipAdsAccessResult = {
  hasAccess: boolean;
  errorMsg: string | null;
};

/**
 * Hook that active-polls the Instagram Paid Partnership Ads authorization
 * status by invoking aspirex's `creatorUpdateIgPartnershipAdsAccess` mutation
 * on a 5s interval. Each call makes aspirex re-query Meta, so the UI can flip
 * from "Waiting for Approval" → "Done" as soon as the creator approves on
 * their Instagram app rather than waiting for the passive enhancements poll.
 *
 * Intentionally paired with `useStandardBriefEnhancementsQuery`'s existing
 * 5s read poll:
 *   - This hook is the *side-effect driver* (writes trigger Meta re-checks).
 *   - The read poll is the *commit detector* (observes
 *     `igPartnershipAdsAccessCompleted` flipping).
 * On `hasAccess: true`, we invalidate `termsKeys.all` so the read poll
 * refetches immediately instead of waiting for its next tick. Source: EPD-7759.
 */
export function useUpdateIgPartnershipAdsAccessPoll(
  organizationId: string,
  profileId: string,
  input: { projectId: string; agreementId: string } | null,
  enabled: boolean,
) {
  const queryClient = useQueryClient();

  return useQuery<UpdateIgPartnershipAdsAccessResult>({
    queryKey: [
      ...termsKeys.all,
      "updatePpaAccess",
      organizationId,
      profileId,
      input?.projectId ?? null,
      input?.agreementId ?? null,
    ] as const,
    enabled: enabled && input !== null,
    refetchInterval: (query) => (query.state.data?.hasAccess ? false : 5000),
    refetchIntervalInBackground: false,
    retry: false,
    queryFn: async () => {
      if (!input) {
        throw new Error(
          "useUpdateIgPartnershipAdsAccessPoll: input is required when enabled",
        );
      }
      const data = (await trpcClient.terms.updateIgPartnershipAdsAccess.mutate({
        organizationId,
        profileId,
        projectId: input.projectId,
        agreementId: input.agreementId,
      })) as UpdateIgPartnershipAdsAccessResult;

      if (data.hasAccess) {
        queryClient.invalidateQueries({ queryKey: termsKeys.all });
      }
      return data;
    },
  });
}

/**
 * Hook for deleting a YouTube BrandConnect video collab.
 */
export function useDeleteYouTubeBrandConnectVideoCollabMutation(
  organizationId: string,
  profileId: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agreementId,
      clientId,
      videoUrl,
    }: {
      agreementId: string;
      clientId: string;
      videoUrl: string;
    }) =>
      trpcClient.terms.deleteYouTubeBrandConnectVideoCollab.mutate({
        organizationId,
        profileId,
        agreementId,
        clientId,
        videoUrl,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
}

/**
 * Hook for agreeing to standard terms with signature.
 */
export function useAgreeToStandardTermsMutation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      secureUrl,
      agreementId,
      memberName,
      signature,
      workItemId,
    }: {
      secureUrl: string;
      agreementId: string;
      memberName: string;
      signature: string;
      workItemId?: string;
    }) =>
      trpcClient.terms.agreeToStandardTerms.mutate({
        organizationId,
        secureUrl,
        agreementId,
        memberName,
        signature,
        workItemId,
      }),
    onSuccess: () => {
      // Invalidate all terms queries to refetch updated state
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
}

/**
 * Input type for proposing standard brief changes.
 */
export interface ProposeStandardBriefChangesInput {
  secureUrl: string;
  clientId: string;
  memberId: number;
  price?: number | null;
  briefUrl?: string | null;
  products?: Array<{
    postType: string;
    count: number;
    requiresApproval: boolean;
    deliverableGuidelines?: {
      post_type?: string;
      name?: string;
      concept?: Array<{ text: string }>;
      concept_title?: string;
      talking_points?: Array<{ text: string }>;
      talking_points_title?: string;
      video_guidelines?: Array<{ text: string }>;
      video_guidelines_title?: string;
      photo_guidelines?: Array<{ text: string }>;
      photo_guidelines_title?: string;
      story_slides_guidelines?: Array<{ text: string }>;
      story_slides_guidelines_title?: string;
      post_guidelines?: Array<{ text: string }>;
      post_guidelines_title?: string;
      link?: Array<{ text: string }>;
      link_title?: string;
      caption_above_read_more?: Array<{ text: string }>;
      caption_above_read_more_title?: string;
      caption_guidelines?: Array<{ text: string }>;
      caption_guidelines_title?: string;
      post_instructions?: Array<{ text: string }>;
      post_instructions_title?: string;
      other_guidelines?: Array<{ text: string }>;
      other_guidelines_title?: string;
      // Preserves each section so multiple custom ones don't collapse on save.
      content_guideline_sections?: Array<{
        type?: string;
        title?: string;
        guidelines?: Array<{ text: string }>;
      }>;
      example_content_guidelines?: Array<{
        media: { url: string; content_type?: string; media_type?: string };
      }>;
    };
    dueDates?: Array<{
      completion?: {
        start_date?: string;
        end_date?: string | null;
        type?: string | null;
        description?: string | null;
        due_date_in_days?: number | null;
      } | null;
      submission?: {
        start_date?: string;
        end_date?: string | null;
        type?: string | null;
        description?: string | null;
        due_date_in_days?: number | null;
      } | null;
    }>;
    acceptedPostTypes?: string[];
    deliverableName?: string[];
  }>;
  contentRightsLanguage?: string | null;
  exclusivityLanguage?: string | null;
  textMessage?: string | null;
  requireContentRights?: boolean;
  requireExclusivity?: boolean;
  requireAdvertiserAccess?: boolean;
  accessStartDate?: string | null;
  accessEndDate?: string | null;
  advertiserSocialAccountIds?: number[] | null;
  creatorContentAgreement?: string[] | null;
  requireTiktokSparkAdsAccess?: boolean;
  tiktokSparkAdsDuration?: number | null;
  usageRightsDuration?: number | null;
  userEmail?: string | null;
  requireInstagramInsights?: boolean;
  isCreatorEdit?: boolean;
  offersPayment?: boolean;
  offersCommission?: boolean;
  offersProduct?: boolean;
  commissionDescription?: string;
  productDescription?: string;
}

/**
 * Hook for proposing changes to a standard brief.
 */
export function useProposeStandardBriefChangesMutation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ProposeStandardBriefChangesInput) =>
      trpcClient.terms.proposeStandardBriefChanges.mutate({
        organizationId,
        ...input,
      }),
    onSuccess: () => {
      // Invalidate terms and project interactions to refetch updated state/stage
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
      queryClient.invalidateQueries({ queryKey: projectsKeys.all });
    },
  });
}
